<template>
  <Container
    id="heading"
  >
    <HeroCard
      :heading="t('jc.view.webDeveloper.hero.title')"
      :paragraph="t('jc.view.webDeveloper.hero.text')"
      :image="{
        src: banner,
        alt: t('jc.view.webDeveloper.hero.banner.alt'),
      }"
      :float="{
        src: float,
        alt: t('jc.view.webDeveloper.hero.float.alt'),
      }"
      :label="t('jc.view.webDeveloper.hero.button.label')"
      :icon="{ prefix: 'fab', iconName: 'discord' }"
      :to="config.public.discordParticipateUrl"
    />
  </Container>

  <Container
    id="our-offer"
  >
    <ContentCard
      :heading="t('jc.view.webDeveloper.ourOffer.title')"
      :paragraph="t('jc.view.webDeveloper.ourOffer.text')"
    >
      <IconWidget
        :items="ourOfferHighlightItems"
        class="my-12"
      />
      <IconList
        :items="ourOfferItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="our-conditions"
  >
    <ContentCard
      :heading="t('jc.view.webDeveloper.ourConditions.title')"
      :paragraph="t('jc.view.webDeveloper.ourConditions.text')"
    >
      <IconList
        :items="ourConditionsItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="your-tasks"
  >
    <ContentCard
      :heading="t('jc.view.webDeveloper.yourTasks.title')"
      :paragraph="t('jc.view.webDeveloper.yourTasks.text')"
    >
      <IconList
        :items="yourTasksItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="impressions"
  >
    <ContentCard
      :heading="t('jc.view.webDeveloper.impressions.title')"
      :paragraph="t('jc.view.webDeveloper.impressions.text')"
    >
      <div
        class="grid grid-cols-1 laptop:grid-cols-2 gap-x-4 gap-y-8"
      >
        <template
          v-for="({ identifier, ...resource }, index) in impressionsResources"
          :key="index"
        >
          <DynamicResource
            :resource="resource"
            :caption="t(`jc.view.webDeveloper.impressions.${identifier}.text`)"
            aspect-video
            rounded
          />
        </template>
      </div>
    </ContentCard>
  </Container>

  <Container
    id="how-to-apply"
  >
    <ContentCard
      :heading="t('jc.view.webDeveloper.howToApply.title')"
      :paragraph="t('jc.view.webDeveloper.howToApply.text')"
    >
      <IconSection
        :sections="howToApplySections"
      />
    </ContentCard>
  </Container>

  <Container
    id="more-information"
  >
    <div
      class="flex flex-col gap-8"
    >
      <ContentCard
        :heading="t('jc.view.webDeveloper.moreInformation.title')"
        :paragraph="t('jc.view.webDeveloper.moreInformation.text')"
      />
      <div
        class="grid grid-cols-1 mobile:gird-cols-2 laptop:grid-cols-3 desktop:grid-cols-4 gap-x-4 gap-y-16 justify-center"
      >
        <template
          v-for="(card, index) in moreInformationCards"
          :key="index"
        >
          <ShowcaseCard
            v-bind="card"
          />
        </template>
      </div>
    </div>
  </Container>

  <Container
    id="callToAction"
    :background="'white'"
  >
    <ParticipateCallCard />
  </Container>
</template>

<script setup lang="ts">
const banner = 'images/component/participateCallCard/background.png'
const float = 'images/skin_renders/roles/web_developer.png'

definePageMeta({
  nameKey: 'participate-webDeveloper',
})

const { t } = useI18n()
const localePath = useLocalePath()
const config = useRuntimeConfig()

const ourOfferHighlightItems = [
  {
    icon: 'users',
    text: t('jc.view.webDeveloper.ourOffer.iconHighlight.atmosphere.text'),
  },
  {
    icon: 'brain-circuit',
    text: t('jc.view.webDeveloper.ourOffer.iconHighlight.newTechniques.text'),
  },
  {
    icon: 'flask-round-potion',
    text: t('jc.view.webDeveloper.ourOffer.iconHighlight.innovativeConcepts.text'),
  },
  {
    icon: 'clock-rotate-left',
    text: t('jc.view.webDeveloper.ourOffer.iconHighlight.versioning.text'),
  },
  {
    icon: 'sidebar-flip',
    text: t('jc.view.webDeveloper.ourOffer.iconHighlight.mockups.text'),
  },
  {
    icon: 'campground',
    text: t('jc.view.webDeveloper.ourOffer.iconHighlight.teamEvents.text'),
  },
]

const ourOfferItems = [
  {
    icon: 'shield-check',
    text: t('jc.view.webDeveloper.ourOffer.icon.securities.text'),
  },
  {
    icon: 'brain-arrow-curved-right',
    text: t('jc.view.webDeveloper.ourOffer.icon.coDecide.text'),
  },
  {
    icon: 'sitemap',
    text: t('jc.view.webDeveloper.ourOffer.icon.hierarchy.text'),
  },
  {
    icon: 'clock',
    text: t('jc.view.webDeveloper.ourOffer.icon.timeManagement.text'),
  },
  {
    icon: 'vial-circle-check',
    text: t('jc.view.webDeveloper.ourOffer.icon.qualityAssurance.text'),
  },
  {
    icon: 'arrows-left-right-to-line',
    text: t('jc.view.webDeveloper.ourOffer.icon.separation.text'),
  },
  {
    icon: 'symbols',
    text: t('jc.view.webDeveloper.ourOffer.icon.componentLibrary.text'),
  },
  {
    icon: 'magnifying-glass-chart',
    text: t('jc.view.webDeveloper.ourOffer.icon.analytics.text'),
  },
  {
    icon: 'magnifying-glass',
    text: t('jc.view.webDeveloper.ourOffer.icon.codeReview.text'),
  },
  {
    icon: 'toolbox',
    text: t('jc.view.webDeveloper.ourOffer.icon.teamTools.text'),
  },
]

const ourConditionsItems = [
  {
    icon: 'couch',
    text: t('jc.view.webDeveloper.ourConditions.icon.teamSpirit.text'),
  },
  {
    icon: 'dna',
    text: t('jc.view.webDeveloper.ourConditions.icon.creativity.text'),
  },
  {
    icon: 'book-open-reader',
    text: t('jc.view.webDeveloper.ourConditions.icon.learn.text'),
  },
  {
    icon: 'gauge-max',
    text: t('jc.view.webDeveloper.ourConditions.icon.motivation.text'),
  },
  {
    icon: 'comment-smile',
    text: t('jc.view.webDeveloper.ourConditions.icon.communication.text'),
  },
  {
    icon: 'battery-full',
    text: t('jc.view.webDeveloper.ourConditions.icon.endurance.text'),
  },
]

const yourTasksItems = [
  {
    icon: 'code-merge',
    text: t('jc.view.webDeveloper.yourTasks.icon.enhance.text'),
  },
  {
    icon: 'eye',
    text: t('jc.view.webDeveloper.yourTasks.icon.visualise.text'),
  },
  {
    icon: 'arrows-cross',
    text: t('jc.view.webDeveloper.yourTasks.icon.create.text'),
  },
  {
    icon: 'link',
    text: t('jc.view.webDeveloper.yourTasks.icon.link.text'),
  },
  {
    icon: 'microscope',
    text: t('jc.view.webDeveloper.yourTasks.icon.test.text'),
  },
  {
    icon: 'info',
    text: t('jc.view.webDeveloper.yourTasks.icon.complement.text'),
  },
]

const impressionsResources = [
  {
    identifier: 'hub',
    mediaType: 'image/*',
    src: 'images/impressions/general/hub.png',
  },
  {
    identifier: 'clashlandsHub',
    mediaType: 'image/*',
    src: 'images/impressions/general/pvp_hub.png',
  },
  {
    identifier: 'dungeonRoom',
    mediaType: 'image/*',
    src: 'images/impressions/general/dungeon_room.png',
  },
  {
    identifier: 'conquestMap',
    mediaType: 'image/*',
    src: 'images/impressions/general/conquest_map.png',
  },
  {
    identifier: 'cosmeticMenu',
    mediaType: 'image/*',
    src: 'images/impressions/general/cosmetic_menu.png',
  },
  {
    identifier: 'clashlandsCannon',
    mediaType: 'image/*',
    src: 'images/impressions/general/cannon.png',
  },
  {
    identifier: 'figma',
    mediaType: 'image/*',
    src: 'images/impressions/web_developer/figma.png',
  },
  {
    identifier: 'mongodb',
    mediaType: 'image/*',
    src: 'images/impressions/web_developer/mongo_db.png',
  },
]

const howToApplySections = [
  {
    icon: 'hand-wave',
    title: t('jc.view.webDeveloper.howToApply.icon.firstContact.title'),
    text: t('jc.view.webDeveloper.howToApply.icon.firstContact.text'),
  },
  {
    icon: 'comments',
    title: t('jc.view.webDeveloper.howToApply.icon.conversation.title'),
    text: t('jc.view.webDeveloper.howToApply.icon.conversation.text'),
  },
  {
    icon: 'envelope',
    title: t('jc.view.webDeveloper.howToApply.icon.wait.title'),
    text: t('jc.view.webDeveloper.howToApply.icon.wait.text'),
  },
  {
    icon: 'user-graduate',
    title: t('jc.view.webDeveloper.howToApply.icon.trainee.title'),
    text: t('jc.view.webDeveloper.howToApply.icon.trainee.text'),
  },
  {
    icon: 'comment-smile',
    title: t('jc.view.webDeveloper.howToApply.icon.feedbackTalk.title'),
    text: t('jc.view.webDeveloper.howToApply.icon.feedbackTalk.text'),
  },
]

const moreInformationCards = [
  {
    icon: 'users',
    text: t('jc.view.webDeveloper.moreInformation.card.team.text'),
    label: t('jc.view.webDeveloper.moreInformation.card.team.button.label'),
    to: localePath({ name: 'team' }),
  },
  {
    icon: 'newspaper',
    text: t('jc.view.webDeveloper.moreInformation.card.weekly_review.text'),
    label: t('jc.view.webDeveloper.moreInformation.card.weekly_review.button.label'),
    to: localePath({ name: 'news' }),
  },
  {
    icon: 'signs-post',
    text: t('jc.view.webDeveloper.moreInformation.card.plan.text'),
    label: t('jc.view.webDeveloper.moreInformation.card.plan.button.label'),
    to: localePath({ name: 'game' }),
  },
  {
    icon: 'microchip',
    text: t('jc.view.webDeveloper.moreInformation.card.stackShare.text'),
    label: t('jc.view.webDeveloper.moreInformation.card.stackShare.button.label'),
    to: config.public.stackShareUrl,
  },
  {
    icon: 'cube',
    text: t('jc.view.webDeveloper.moreInformation.card.planetMinecraft.text'),
    label: t('jc.view.webDeveloper.moreInformation.card.planetMinecraft.button.label'),
    to: config.public.planetMinecraftUrl,
  },
  {
    icon: 'file-lines',
    text: t('jc.view.webDeveloper.moreInformation.card.blog.text'),
    label: t('jc.view.webDeveloper.moreInformation.card.blog.button.label'),
    to: config.public.scrayosUrl,
  },
]
</script>
